export type EngineItemError = {
  rfid_code: string
  error: string
  code: string
  sku_code?: string
}

export type EngineApiError = {
  message?: string
  statusCode?: number
  code?: string
  errors?: EngineItemError[]
  results?: unknown[]
}

export type EngineErrorLabels = {
  alreadyTagged?: string
  alreadyTaggedBirth?: string
  alreadyTaggedTag?: string
  invalidEncoding?: string
  notBirthed?: string
  invalidSku?: string
  birthPartial?: string
  tagPartial?: string
  allFailed?: string
  allOk?: string
}

export function isEngineBatchPayload(
  value: unknown
): value is { results?: unknown[]; errors: EngineItemError[] } {
  return (
    !!value &&
    typeof value === 'object' &&
    Array.isArray((value as { errors?: unknown }).errors)
  )
}

export function engineItemErrorLabel(
  item: Pick<EngineItemError, 'code' | 'error'>,
  labels?: EngineErrorLabels
): string {
  switch (item.code) {
    case 'ALREADY_TAGGED':
      return labels?.alreadyTagged ?? 'Already tagged with a SKU'
    case 'INVALID_ENCODING':
      return labels?.invalidEncoding ?? 'Invalid RFID encoding'
    case 'NOT_BIRTHED':
      return labels?.notBirthed ?? 'Not birthed yet — birth the tag first'
    case 'INVALID_SKU':
      return labels?.invalidSku ?? 'Invalid SKU'
    default:
      return item.error || item.code || 'Unknown error'
  }
}

export function summarizeEngineBatch(
  kind: 'birth' | 'tag',
  resultsCount: number,
  errors: EngineItemError[],
  labels?: EngineErrorLabels
): { tone: 'success' | 'warning' | 'error'; message: string } {
  const failed = errors.length
  const alreadyTagged = errors.filter((e) => e.code === 'ALREADY_TAGGED').length

  if (failed === 0) {
    return {
      tone: 'success',
      message: (labels?.allOk ?? '{{count}} ok').replace('{{count}}', String(resultsCount)),
    }
  }

  if (resultsCount === 0 && alreadyTagged === failed) {
    const template =
      kind === 'birth'
        ? (labels?.alreadyTaggedBirth ??
          '{{count}} tag(s) already have a SKU — birth is only for untagged codes')
        : (labels?.alreadyTaggedTag ?? '{{count}} tag(s) could not be mapped')
    return { tone: 'error', message: template.replace('{{count}}', String(failed)) }
  }

  if (resultsCount === 0) {
    return {
      tone: 'error',
      message: (labels?.allFailed ?? '{{failed}} failed').replace('{{failed}}', String(failed)),
    }
  }

  const partial =
    kind === 'birth'
      ? (labels?.birthPartial ?? '{{ok}} birthed, {{failed}} failed')
      : (labels?.tagPartial ?? '{{ok}} mapped, {{failed}} failed')

  return {
    tone: 'warning',
    message: partial
      .replace('{{ok}}', String(resultsCount))
      .replace('{{failed}}', String(failed)),
  }
}

export function singleEngineErrorMessage(
  err: EngineApiError,
  labels?: EngineErrorLabels
): string {
  if (err.code === 'ALREADY_TAGGED') {
    return labels?.alreadyTagged ?? err.message ?? 'Already tagged with a SKU'
  }
  if (err.code === 'INVALID_ENCODING') {
    return labels?.invalidEncoding ?? err.message ?? 'Invalid RFID encoding'
  }
  if (err.code === 'NOT_BIRTHED') {
    return labels?.notBirthed ?? err.message ?? 'Not birthed yet — birth the tag first'
  }
  return err.message || 'Engine request failed'
}
